import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Printer, FileDown } from "lucide-react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

export default function WeeklyMIS() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/mis/weekly").then((r) => setData(r.data)); }, []);

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      ["Section", "Metric", "Value"],
      ["Summary", "Inspections", data.inspections_count],
      ["Summary", "Defects Critical", data.defect_summary.Critical],
      ["Summary", "Defects Major", data.defect_summary.Major],
      ["Summary", "Defects Minor", data.defect_summary.Minor],
      [],
      ["Delayed POs", "PO", "ETA", "Pace", "% Complete"],
      ...data.delayed_pos.map((p) => ["Delayed", p.po_number, p.eta, p.pace_flag, p.percent_complete]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "weekly_mis.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const W = doc.internal.pageSize.getWidth();
    const ws = new Date(data.week_start).toLocaleDateString();
    const we = new Date(data.week_end).toLocaleDateString();

    // Header
    doc.setFillColor(11, 35, 64);
    doc.rect(0, 0, W, 60, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16); doc.setFont(undefined, "bold");
    doc.text("HAG Carpets · Weekly MIS", 40, 30);
    doc.setFontSize(10); doc.setFont(undefined, "normal");
    doc.text(`Week: ${ws} — ${we}`, 40, 46);
    doc.setFontSize(9);
    doc.text(`Generated ${new Date().toLocaleString()}`, W - 40, 30, { align: "right" });

    // KPI row
    doc.setTextColor(28, 46, 76);
    let y = 90;
    const tiles = [
      ["Inspections", data.inspections_count],
      ["Critical",   data.defect_summary.Critical],
      ["Major",      data.defect_summary.Major],
      ["Minor",      data.defect_summary.Minor],
    ];
    const tw = (W - 80 - 30) / 4;
    tiles.forEach(([label, val], i) => {
      const x = 40 + i * (tw + 10);
      doc.setDrawColor(230, 224, 210);
      doc.setFillColor(250, 246, 240);
      doc.roundedRect(x, y, tw, 60, 4, 4, "FD");
      doc.setFontSize(8); doc.setTextColor(124, 142, 158);
      doc.text(String(label).toUpperCase(), x + 10, y + 18);
      doc.setFontSize(22); doc.setFont(undefined, "bold"); doc.setTextColor(11, 35, 64);
      doc.text(String(val ?? "-"), x + 10, y + 50);
      doc.setFont(undefined, "normal");
    });

    y += 90;
    doc.setFontSize(12); doc.setFont(undefined, "bold");
    doc.text("Delayed POs", 40, y);

    // Delayed POs table
    autoTable(doc, {
      startY: y + 8,
      head: [["PO", "ETA", "% Complete", "Pace"]],
      body: data.delayed_pos.map((p) => [
        p.po_number,
        p.eta ? new Date(p.eta).toLocaleDateString() : "-",
        `${p.percent_complete}%`,
        p.pace_flag,
      ]),
      styles: { fontSize: 9, cellPadding: 5 },
      headStyles: { fillColor: [27, 58, 92], textColor: 255 },
      alternateRowStyles: { fillColor: [250, 246, 240] },
      margin: { left: 40, right: 40 },
    });

    // Footer page numbers
    const pages = doc.getNumberOfPages();
    for (let i = 1; i <= pages; i++) {
      doc.setPage(i);
      doc.setFontSize(8); doc.setTextColor(124, 142, 158);
      doc.text(`Page ${i} of ${pages}`, W - 40, doc.internal.pageSize.getHeight() - 20, { align: "right" });
      doc.text("HAG Carpets Pvt. Ltd. · Mirzapur", 40, doc.internal.pageSize.getHeight() - 20);
    }

    doc.save(`weekly_mis_${ws.replace(/\//g, "-")}.pdf`);
  };

  if (!data) return <div>{t("loading")}</div>;

  return (
    <div className="max-w-4xl">
      <div className="eyebrow">{t("nav_weekly_mis")}</div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold" style={{ color: "var(--brand-navy)" }}>Weekly MIS</h1>
        <div className="flex gap-2">
          <button data-testid="mis-export-pdf" onClick={exportPdf}
            className="h-10 px-3 rounded-md text-sm flex items-center gap-1 text-white"
            style={{ background: "var(--brand-navy)" }}>
            <FileDown size={14} /> {t("export_pdf")}
          </button>
          <button data-testid="mis-print-btn" onClick={() => window.print()} className="h-10 px-3 rounded-md border border-[#1B3A5C]/15 text-sm flex items-center gap-1"><Printer size={14} /> Print</button>
          <button data-testid="mis-export-excel" onClick={exportCsv} className="h-10 px-3 rounded-md border border-[#1B3A5C]/15 text-sm">{t("export_excel")}</button>
        </div>
      </div>
      <div className="text-sm text-[#4A627A] mt-1">
        Week: {new Date(data.week_start).toLocaleDateString()} – {new Date(data.week_end).toLocaleDateString()}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
        <Stat label="Inspections" v={data.inspections_count} />
        <Stat label="Critical Defects" v={data.defect_summary.Critical} />
        <Stat label="Major Defects" v={data.defect_summary.Major} />
        <Stat label="Minor Defects" v={data.defect_summary.Minor} />
      </div>

      <h2 className="mt-6 font-bold" style={{ color: "var(--brand-navy)" }}>Delayed POs</h2>
      <div className="mt-2 hag-surface overflow-x-auto hag-scroll">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6F0] text-[#4A627A] uppercase text-xs tracking-widest">
            <tr><th className="p-3 text-left">PO</th><th className="p-3 text-left">ETA</th><th className="p-3 text-left">%</th><th className="p-3 text-left">Pace</th></tr>
          </thead>
          <tbody>
            {data.delayed_pos.map((p) => (
              <tr key={p.po_number} data-testid={`mis-delayed-${p.po_number}`} className="border-t border-[#1B3A5C]/5">
                <td className="p-3 font-semibold">{p.po_number}</td>
                <td className="p-3">{p.eta ? new Date(p.eta).toLocaleDateString() : "—"}</td>
                <td className="p-3">{p.percent_complete}%</td>
                <td className="p-3"><span className={`status-chip ${p.pace_flag === "red" ? "pace-red" : "pace-amber"}`}>{p.pace_flag}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="hag-surface p-4">
      <div className="text-xs uppercase tracking-widest text-[#7C8E9E]">{label}</div>
      <div className="text-3xl font-black mt-1" style={{ color: "var(--brand-navy)" }}>{v}</div>
    </div>
  );
}
